import { useFormikSubmit } from "@certn/api";
import {
  ButtonGroup,
  Grid,
  GridItem,
  PrimaryButton,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { formatName, parseName } from "@certn/utils";
import { Form, Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import { useProfile } from "../../../auth/ProfileProvider";
import { SettingsEditItem } from "../../../components/settings-edit/SettingsEditItem";

export function NameSettings() {
  const { t } = useTranslation();
  const { profile, refresh } = useProfile();
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((curr) => !curr);

  const name = profile.profilename_set[0];

  const { submit } = useFormikSubmit(
    `/api/internal/applicant/me/personal-info/name/${name.id}/update/`,
    {
      onSuccess: () => {
        toggle();
        refresh();
      },
      showToastOnError: true,
    }
  );

  // 1+ letters with spaces, apostrophes and hyphens allowed anywhere except first and last character
  const validNameRegex = /^((\p{L}+)|(\p{L}[\p{L}\s'-]*\p{L}))$/u;
  const validationText = t("Please only use letters");

  return (
    <SettingsEditItem
      setting={t("Legal name")}
      value={formatName(parseName(name))}
      description={t(
        "We recommend using the information on a government issued identity document or birth certificate as this may be used to auto-populate forms."
      )}
      expanded={expanded}
      toggle={toggle}
      testID={expanded ? "legal-name-cancel-link" : "legal-name-edit-link"}
    >
      <Formik
        initialValues={{
          given_name: name.given_name,
          additional_name: name.additional_name,
          family_name: name.family_name,
        }}
        onSubmit={submit}
        validationSchema={object({
          given_name: string()
            .trim()
            .required()
            .matches(validNameRegex, validationText)
            .label(t("Given name")),
          additional_name: string()
            .trim()
            .matches(validNameRegex, validationText)
            .label(t("Additional given name")),
          family_name: string()
            .trim()
            .required()
            .matches(validNameRegex, validationText)
            .label(t("Family name")),
        })}
      >
        {({ handleSubmit }) => {
          return (
            <Form>
              <Grid width="416px" form>
                <GridItem>
                  <TextInputFormikField
                    testID="given-name"
                    name="given_name"
                    label={t("Given name")}
                    isRequired
                  />
                </GridItem>
                <GridItem>
                  <TextInputFormikField
                    testID="additional-name"
                    name="additional_name"
                    label={t("Additional given name")}
                  />
                </GridItem>
                <GridItem>
                  <TextInputFormikField
                    testID="family-name"
                    name="family_name"
                    label={t("Family name")}
                    isRequired
                  />
                </GridItem>
                <GridItem>
                  <ButtonGroup>
                    <PrimaryButton
                      onPress={handleSubmit}
                      size="medium"
                      testID="legal-name-save-button"
                    >
                      {t("Save")}
                    </PrimaryButton>
                  </ButtonGroup>
                </GridItem>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </SettingsEditItem>
  );
}
